import { ShoppingListItem, Product, ExternalBarcodeLookupResponse } from "./grocyApi.ts";
import { isAIConfigured, requestClassification } from "./aiClient.ts";

export type UnknownProductSuggestionMode = "create" | "parent" | "link";

export interface UnknownProductSuggestionCandidate {
  id: number;
  name: string;
  description: string | null;
  parentProductId: number | null;
  parentName: string | null;
  noOwnStock: boolean;
  hasChildren: boolean;
  childCount: number;
  onActiveShoppingList: boolean;
  shoppingListAmount: number;
  matchScore: number;
}

export interface UnknownProductSuggestionResult {
  mode: UnknownProductSuggestionMode;
  productId: number | null;
  confidence: number;
  reason: string;
}

const MAX_CANDIDATES = 30;
const MIN_CONFIDENCE = 0.72;

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenize(value: string): string[] {
  return normalizeText(value)
    .split(" ")
    .filter((token) => token.length >= 2);
}

function countOverlap(a: string[], b: string[]): number {
  const bSet = new Set(b);
  return a.filter((token) => bSet.has(token)).length;
}

export function buildUnknownProductSuggestionCandidates(
  unknownProduct: ExternalBarcodeLookupResponse,
  products: Product[],
  shoppingListItems: ShoppingListItem[],
): UnknownProductSuggestionCandidate[] {
  const productsById = new Map(products.map((product) => [product.id, product]));
  const childCountById = new Map<number, number>();

  for (const product of products) {
    if (product.parent_product_id) {
      childCountById.set(
        product.parent_product_id,
        (childCountById.get(product.parent_product_id) ?? 0) + 1,
      );
    }
  }

  const activeListItems = shoppingListItems.filter((item) => !item.done);
  const shoppingListAmountByProductId = new Map<number, number>();
  for (const item of activeListItems) {
    shoppingListAmountByProductId.set(
      item.product_id,
      (shoppingListAmountByProductId.get(item.product_id) ?? 0) + item.amount,
    );
  }

  const activeProductIds = new Set(shoppingListAmountByProductId.keys());
  const relatedProductIds = new Set<number>();
  for (const productId of activeProductIds) {
    const product = productsById.get(productId);
    if (!product) continue;
    relatedProductIds.add(product.id);
    if (product.parent_product_id) {
      relatedProductIds.add(product.parent_product_id);
    }
    for (const child of products) {
      if (child.parent_product_id === product.id) {
        relatedProductIds.add(child.id);
      }
    }
  }

  const unknownTokens = tokenize(unknownProduct.name);
  const normalizedUnknownName = normalizeText(unknownProduct.name);

  const ranked = products.map((product) => {
    const normalizedProductName = normalizeText(product.name);
    const productTokens = tokenize(product.name);
    const overlap = countOverlap(unknownTokens, productTokens);
    const onActiveShoppingList = activeProductIds.has(product.id);
    const relatedToShoppingList = relatedProductIds.has(product.id);
    const exactNameMatch = normalizedProductName === normalizedUnknownName;
    const partialNameMatch = normalizedProductName.includes(normalizedUnknownName)
      || normalizedUnknownName.includes(normalizedProductName);
    const childCount = childCountById.get(product.id) ?? 0;
    const hasChildren = childCount > 0;
    const parentName = product.parent_product_id
      ? productsById.get(product.parent_product_id)?.name ?? null
      : null;

    let matchScore = 0;
    if (onActiveShoppingList) matchScore += 120;
    if (relatedToShoppingList) matchScore += 50;
    if (exactNameMatch) matchScore += 90;
    if (partialNameMatch) matchScore += 45;
    matchScore += overlap * 18;
    if (product.no_own_stock === 1) matchScore += 10;
    if (hasChildren) matchScore += Math.min(childCount, 5) * 4;

    return {
      id: product.id,
      name: product.name,
      description: product.description,
      parentProductId: product.parent_product_id,
      parentName,
      noOwnStock: product.no_own_stock === 1,
      hasChildren,
      childCount,
      onActiveShoppingList,
      shoppingListAmount: shoppingListAmountByProductId.get(product.id) ?? 0,
      matchScore,
    } satisfies UnknownProductSuggestionCandidate;
  });

  return ranked
    .sort((a, b) => {
      if (b.matchScore !== a.matchScore) return b.matchScore - a.matchScore;
      if (a.onActiveShoppingList !== b.onActiveShoppingList) {
        return a.onActiveShoppingList ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    })
    .slice(0, MAX_CANDIDATES);
}

function extractJsonObject(value: string): string {
  const cleaned = value.replace(/```json\s*|\s*```/g, "").trim();
  const match = cleaned.match(/\{[\s\S]*\}/);
  return match ? match[0] : cleaned;
}

export function normalizeUnknownProductSuggestionResponse(
  response: string,
  candidates: UnknownProductSuggestionCandidate[],
): UnknownProductSuggestionResult | null {
  const parsed = JSON.parse(extractJsonObject(response)) as Partial<
    UnknownProductSuggestionResult
  >;
  const mode = parsed.mode;
  const confidence = typeof parsed.confidence === "number"
    ? parsed.confidence
    : 0;
  const productId = typeof parsed.productId === "number" ? parsed.productId : null;
  const reason = typeof parsed.reason === "string" ? parsed.reason.trim() : "";
  const validModes: UnknownProductSuggestionMode[] = ["create", "parent", "link"];

  if (!mode || !validModes.includes(mode)) {
    return null;
  }

  if (confidence < MIN_CONFIDENCE) {
    return {
      mode: "create",
      productId: null,
      confidence,
      reason,
    };
  }

  if (mode === "create") {
    return {
      mode,
      productId: null,
      confidence,
      reason,
    };
  }

  const candidateExists = candidates.some((candidate) => candidate.id === productId);
  if (!candidateExists || productId === null) {
    return {
      mode: "create",
      productId: null,
      confidence,
      reason,
    };
  }

  return {
    mode,
    productId,
    confidence,
    reason,
  };
}

export async function suggestUnknownProductAction(
  unknownProduct: ExternalBarcodeLookupResponse,
  products: Product[],
  shoppingListItems: ShoppingListItem[],
): Promise<UnknownProductSuggestionResult | null> {
  if (!isAIConfigured()) {
    return null;
  }

  const candidates = buildUnknownProductSuggestionCandidates(
    unknownProduct,
    products,
    shoppingListItems,
  );

  if (candidates.length === 0) {
    return null;
  }

  const prompt = `You are deciding how to handle a newly scanned grocery barcode.

Scanned product:
${JSON.stringify({
    name: unknownProduct.name,
    barcode: unknownProduct.__barcode,
  })}

Choose one of:
- "create": this is a new standalone product and should not be linked or parented
- "parent": create a new product and set parent_product_id to the chosen candidate product
- "link": attach the scanned barcode directly to the chosen existing product

Rules:
- "link" is only allowed when the scanned item is the same underlying product as the chosen candidate.
- Use "parent" for variants, flavors, package sizes, or sub-products that should live under a parent/family product.
- Prefer candidates on the active shopping list when they clearly fit.
- If no candidate is a strong fit, choose "create" with productId null.
- Return only one candidate productId from the list below, never invent ids.

Candidates:
${JSON.stringify(candidates)}

Respond with strict JSON:
{
  "mode": "create" | "parent" | "link",
  "productId": number | null,
  "confidence": 0.0-1.0,
  "reason": "short explanation"
}`;

  try {
    const response = await requestClassification(prompt);
    return normalizeUnknownProductSuggestionResponse(response, candidates);
  } catch (error) {
    console.error("Unknown product suggestion failed:", error);
    return null;
  }
}
